import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import { ProcessRiskControlData } from 'store/ducks/Reporting/types';
import dayjs from 'dayjs';
import CustomTable from 'components/Tables/CustomTable';
import ApiService from 'utils/ApiCalls';
import * as Styled from 'components/Layouts/Layout.styled';
import * as LocalStyled from './RisksControlsGaps.styled';
import * as Constants from './RisksControlsGaps.constants';
import * as ReportingStyled from '../Reporting.styled';
import { useReportExport } from 'utils/hooks/useReportExport';

const { useToken } = theme;

const RisksControlsGaps: React.FC = () => {
  const { token } = useToken();

  const [processRiskControlData, setProcessRiskControlData] = useState<
    ProcessRiskControlData[]
  >([]);

  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

  const [shouldFetch, setShouldFetch] = useState(false);

  const [filters, setFilters] = useState<{
    group_ids?: string;
    division_ids?: string;
    region_ids?: string;
    legal_entity_ids?: string;
    business_unit_ids?: string;
  }>({});

  const [groupOptions, setGroupOptions] = useState<
    { label: string; value: number }[]
  >([]);

  const [divisionOptions, setDivisionOptions] = useState<
    { label: string; value: number }[]
  >([]);

  const [regionOptions, setRegionOptions] = useState<
    { label: string; value: number }[]
  >([]);

  const [legalEntityOptions, setLegalEntityOptions] = useState<
    { label: string; value: number }[]
  >([]);

  const [businessUnitOptions, setBusinessUnitOptions] = useState<
    { label: string; value: number }[]
  >([]);

  const { scheme } = useSelector((state: RootState) => ({
    scheme: state.global.scheme
  }));

  const { t } = useTranslation();

  const { registerReport, clearReport } = useReportExport();

  const columns = useMemo(
    () => Constants.processRiskControlColumns(processRiskControlData),
    [processRiskControlData]
  );

  useEffect(() => {
    registerReport(
      processRiskControlData,
      columns,
      'Risks_Controls_Gaps',
      'Risks Controls Gaps'
    );
  }, [processRiskControlData, columns, registerReport]);

  useEffect(() => {
    return () => {
      clearReport();
    };
  }, [clearReport]);

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const [groups, divisions, regions, legalEntities, businessUnits] =
          await Promise.all([
            ApiService.getGroupBPR(),
            ApiService.getDivisionBPR(),
            ApiService.getRegionBPR(),
            ApiService.getLegalEntitiesBPR(),
            ApiService.getBusinessUnitBPR()
          ]);

        setGroupOptions(
          groups.data.data.map((g: { id: number; name: string }) => ({
            label: g.name,
            value: g.id
          }))
        );

        setDivisionOptions(
          divisions.data.data.map((d: { id: number; name: string }) => ({
            label: d.name,
            value: d.id
          }))
        );

        setRegionOptions(
          regions.data.data.map((r: { id: number; name: string }) => ({
            label: r.name,
            value: r.id
          }))
        );

        setLegalEntityOptions(
          legalEntities.data.data.map((l: { id: number; name: string }) => ({
            label: l.name,
            value: l.id
          }))
        );

        setBusinessUnitOptions(
          businessUnits.data.data.map((b: { id: number; name: string }) => ({
            label: b.name,
            value: b.id
          }))
        );
      } catch (error) {
        console.error('Failed to fetch dropdown options:', error);
      }
    };

    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    const fetchRisksControlsGaps = async () => {
      setLoading(true);

      try {
        const params: string[] = [];

        if (filters.group_ids) {
          params.push(`group_ids=${filters.group_ids}`);
        }

        if (filters.division_ids) {
          params.push(`division_ids=${filters.division_ids}`);
        }

        if (filters.region_ids) {
          params.push(`region_ids=${filters.region_ids}`);
        }

        if (filters.legal_entity_ids) {
          params.push(`legal_entity_ids=${filters.legal_entity_ids}`);
        }

        if (filters.business_unit_ids) {
          params.push(`business_unit_ids=${filters.business_unit_ids}`);
        }

        if (selectedDate) {
          params.push(`date=${selectedDate.format('YYYY-MM-DD')}`);
        }

        const response = await ApiService.getProcessRiskControl(
          params.length > 0 ? params.join('&') : undefined
        );

        if (
          response.data?.status === 'success' &&
          Array.isArray(response.data.data)
        ) {
          const dataWithKeys = response.data.data.map(
            (item: ProcessRiskControlData, index: number) => ({
              ...item,
              key: `${item.business_process_l4_id}_${item.risk_id}_${item.control_id}_${index}`
            })
          );

          setProcessRiskControlData(dataWithKeys);
        } else {
          setProcessRiskControlData([]);
        }
      } catch (error) {
        console.error('Failed to fetch risks, controls and gaps:', error);

        setProcessRiskControlData([]);
      } finally {
        setLoading(false);
        setShouldFetch(false);
      }
    };

    fetchRisksControlsGaps();
  }, [selectedDate, shouldFetch, filters]);

  const handleDisplay = () => {
    setShouldFetch(true);
  };

  return (
    <>
      <LocalStyled.GlobalDatePickerStyle token={token} />

      <ReportingStyled.ReportTableWrapper>
        <LocalStyled.RisksControlsGapsFilterBar>
          <Styled.HorizontalFilterBar token={token} scheme={scheme}>
            <Styled.FilteredContainerRisks>
              <LocalStyled.EntitySelect
                mode='multiple'
                placeholder={t('GROUP', 'Group')}
                options={groupOptions}
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    group_ids:
                      Array.isArray(value) && value.length > 0
                        ? value.join(',')
                        : undefined
                  })
                }
                allowClear
              />

              <LocalStyled.EntitySelect
                mode='multiple'
                placeholder={t('DIVISION', 'Division')}
                options={divisionOptions}
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    division_ids:
                      Array.isArray(value) && value.length > 0
                        ? value.join(',')
                        : undefined
                  })
                }
                allowClear
              />

              <LocalStyled.EntitySelect
                mode='multiple'
                placeholder={t('REGION', 'Region')}
                options={regionOptions}
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    region_ids:
                      Array.isArray(value) && value.length > 0
                        ? value.join(',')
                        : undefined
                  })
                }
                allowClear
              />

              <LocalStyled.EntitySelect
                mode='multiple'
                placeholder={t('LEGALENTITY', 'Legal Entity')}
                options={legalEntityOptions}
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    legal_entity_ids:
                      Array.isArray(value) && value.length > 0
                        ? value.join(',')
                        : undefined
                  })
                }
                allowClear
              />

              <LocalStyled.EntitySelect
                mode='multiple'
                placeholder={t('BUSINESSUNIT', 'Business Unit')}
                options={businessUnitOptions}
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    business_unit_ids:
                      Array.isArray(value) && value.length > 0
                        ? value.join(',')
                        : undefined
                  })
                }
                allowClear
              />

              <LocalStyled.StyledDatePicker
                placeholder={t('DATE', 'Date')}
                value={selectedDate}
                onChange={(date) => setSelectedDate(date as dayjs.Dayjs | null)}
                format='YYYY-MM-DD'
                allowClear
                popupClassName='white-bg-datepicker'
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              />

              <Styled.Spacer />

              <Styled.StyledButton
                type='primary'
                onClick={handleDisplay}
                className='display-save-button'
              >
                {t('DISPLAY', 'Display')}
              </Styled.StyledButton>
            </Styled.FilteredContainerRisks>
          </Styled.HorizontalFilterBar>
        </LocalStyled.RisksControlsGapsFilterBar>

        <LocalStyled.RisksControlsGapsTableWrapper>
          <CustomTable
            drawTable
            itemsCount={processRiskControlData.length}
            columns={columns}
            dataSource={processRiskControlData}
            loading={loading}
            rowKey='key'
            scroll={{
              x: 5000,
              y: 350
            }}
            sticky
            scheme={scheme}
          />
        </LocalStyled.RisksControlsGapsTableWrapper>
      </ReportingStyled.ReportTableWrapper>
    </>
  );
};

export default RisksControlsGaps;
